import { Scrollbar } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { memo, useId } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useOnDragEnd } from './hooks/use-on-drag-end';
import * as Styled from './styles';
import * as SX from './sx';
import type { SortableTableProps as Props } from './types';

export const SortableTable = memo(
  ({
    className,
    items,
    selectable,
    columns,
    onChange,
    renderItem,
    loading,
  }: Props) => {
    const id = useId();
    const onDragEnd = useOnDragEnd(items, onChange);

    return (
      <Styled.Wrapper onDragEnd={onDragEnd}>
        <Droppable droppableId={id}>
          {(drop) => (
            <Styled.List
              className={classnames('SortableList', className, {
                Loading: loading,
              })}
              ref={drop.innerRef}
              {...drop.droppableProps}
            >
              <Stack
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                pl={5}
                mb={0.5}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={SX.titleContainer}
                >
                  {columns.map((col) => (
                    <Stack key={String(col.key)} sx={col.sx}>
                      <Typography variant="body2" sx={col.headerCellSx}>
                        {col.label}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
              <Scrollbar sx={{ height: '70vh' }}>
                {items.map((x, index) => (
                  <Draggable draggableId={x.id} index={index} key={x.id}>
                    {(drag, { isDragging }) => (
                      <Styled.Item {...drag.draggableProps} ref={drag.innerRef}>
                        <Paper>
                          <Stack
                            direction={selectable ? 'row' : 'row-reverse'}
                            justifyContent="space-between"
                            alignItems="center"
                            px={1}
                            py={0.5}
                            gap={2}
                            sx={{
                              backgroundColor: (p) => p.palette.grey[100],
                              borderRadius: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={SX.item(isDragging)}
                            >
                              <Box sx={SX.item(isDragging)}>
                                {renderItem ? renderItem(x) : x.name}
                              </Box>
                            </Stack>

                            <Box {...drag.dragHandleProps} display="flex">
                              <Styled.Handler name="reorder" size={18} />
                            </Box>
                          </Stack>
                        </Paper>
                      </Styled.Item>
                    )}
                  </Draggable>
                ))}
                {drop.placeholder}
              </Scrollbar>
            </Styled.List>
          )}
        </Droppable>
      </Styled.Wrapper>
    );
  }
);

SortableTable.displayName = 'SortableTable';
